import { useCallback } from 'react';

import { useShopProvider } from '#provider/useShopProvider.js';
import { useShopRoutes } from '#routes/useShopRoutes.js';
import { Cart, CartItem, useCreateCart, useCreateConsumerCheckout } from '@givebacks/api/store';
import { getErrorMessage } from '@givebacks/utils';
import { DeepPartial } from 'core';
import { notifications } from 'ui';

interface StoreCartItems extends DeepPartial<Omit<CartItem, 'options'>> {}
interface CreateCartAndGoToCheckoutArgs {
  storeId: string;
  cartItems: StoreCartItems[];
  /** Cart Level Fields that get attached to the `cart` on send  */
  cartFields?: DeepPartial<Cart>;
  mutationArgs?: Parameters<ReturnType<typeof useCreateCart>['mutate']>[1];
}

const useCreateCartAndGoToCheckout = () => {
  const { navigateToCheckout } = useShopRoutes();
  const { provider } = useShopProvider();
  const { mutate: createCart, isPending: isCreateLoading } = useCreateCart();
  // Create a checkout object
  const { mutate: createConsumerCheckout, isPending: isCreateConsumerCheckoutLoading } =
    useCreateConsumerCheckout();

  const createCheckoutAndNavigateToCheckout = useCallback(
    (cartId: string) => {
      createConsumerCheckout(
        {
          checkout: {
            pending_cart_ids: [cartId],
          },
        },
        {
          onSuccess: (response) => {
            navigateToCheckout(response.checkout.uuid);
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage(error, 'Error creating checkout'),
              variant: 'error',
            });
          },
        }
      );
    },
    [createConsumerCheckout, navigateToCheckout]
  );

  const createCartAndGoToCheckout = useCallback(
    ({ storeId, cartItems, cartFields, mutationArgs }: CreateCartAndGoToCheckoutArgs) => {
      createCart(
        { causeId: storeId, cart_items: cartItems, provider, ...cartFields },
        {
          ...mutationArgs,
          onSuccess: (cart, variables, context) => {
            mutationArgs?.onSuccess?.(cart, variables, context);
            createCheckoutAndNavigateToCheckout(cart.uuid);
          },
        }
      );
    },
    [createCart, createCheckoutAndNavigateToCheckout, provider]
  );

  return {
    isLoading: isCreateLoading || isCreateConsumerCheckoutLoading,
    createCartAndGoToCheckout,
  };
};

export { useCreateCartAndGoToCheckout };
